import Swal from "sweetalert2";
import { Col, Row } from 'react-bootstrap';
import Panel from "@/shared/components/Panel";
import { styled } from "@mui/material/styles";
import React, { useState, useEffect } from "react";
import AdminInterface from '@/utils/AdminInterface';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import withReactContent from "sweetalert2-react-content";
import am4themes_frozen from "@amcharts/amcharts4/themes/frozen";
import * as am4plugins_regression from "@amcharts/amcharts4/plugins/regression";

const StyledPaper = styled('div')(({ theme }) => ({
    color: 'black',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #000',
    boxShadow: theme.shadows[5],
}));

const APIUsageDetails = (props) => {
    const [userName, setUserName] = useState();
    const [displayChart, setDisplayChart] = useState();

    const handleModalClose = () => {
        displayChart.dispose();
        props.setSelectedAPIUsage(null);
    };

    const createDailyUsageChart = (data) => {
        if (displayChart !== undefined) {
            displayChart.dispose();
        }

        am4core.useTheme(am4themes_frozen);
        let chart = am4core.create("daily_usage_chart", am4charts.XYChart);
        setDisplayChart(chart);
        chart.data = data;
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = "Day";

        chart.yAxes.push(new am4charts.ValueAxis());
        let series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = "# Request";
        series.dataFields.categoryX = "Day";
        series.name = "# Request";
        series.columns.template.tooltipText = "{categoryX}: [bold]{valueY}[/]";
        series.columns.template.strokeOpacity = 0;
        series.columns.template.column.cornerRadiusTopLeft = 2;
        series.columns.template.column.cornerRadiusTopRight = 2;

        let regseries = chart.series.push(new am4charts.LineSeries());
        regseries.dataFields.valueY = "# Request";
        regseries.dataFields.categoryX = "Day";
        regseries.strokeWidth = 1;
        regseries.name = "Regression Line";

        let regressor = regseries.plugins.push(new am4plugins_regression.Regression());
        regressor.method = "polynomial";
        regseries.strokeDasharray = "3,3";
        regseries.stroke = am4core.color("#eb6eb0");
    }

    const createMissionsUsageChart = (data) => {
        am4core.useTheme(am4themes_frozen);
        let chart = am4core.create("mission_usage_chart", am4charts.PieChart3D);
        chart.hiddenState.properties.opacity = 0;
        chart.data = data;
        chart.depth = 10;
        
        let pieSeries = chart.series.push(new am4charts.PieSeries3D());
        chart.innerRadius = am4core.percent(40);
        pieSeries.dataFields.value = "# Request";
        pieSeries.dataFields.category = "Mission";

        const totalRequests = data.reduce((sum, item) => sum + item["# Request"], 0);
        pieSeries.labels.template.adapter.add("disabled", (disabled, target) => {
            if (target.dataItem) {
                const value = target.dataItem.values.value.value;
                return (value / totalRequests) < 0.02;
            }
            return disabled;
        });

        pieSeries.ticks.template.adapter.add("disabled", (disabled, target) => {
            if (target.dataItem) {
                const value = target.dataItem.values.value.value;
                return (value / totalRequests) < 0.02;
            }
            return disabled;
        });

        let groupedData = [];
        let otherSum = 0;
        data.forEach(item => {
            if ((item["# Request"] / totalRequests) < 0.01) {
                otherSum += item["# Request"];
            } else {
                groupedData.push(item);
            }
        });
        if (otherSum > 0) {
            groupedData.push({ "Mission": "Others", "# Request": otherSum });
        }
        chart.data = groupedData;
    }

    const getDailySum = (data) => {
        const groups = data.reduce((map, el) => {
            const dateKey = new Date(`${el.to_datetime}z`).toLocaleDateString('en')
            map.set(dateKey, map.get(dateKey) ? map.get(dateKey) + el.count : el.count);
            return map;
        }, new Map());
        return Array.from(groups.entries());
    }

    const GetAPIUsageDetail = (apiUsage) => {
        AdminInterface.instance.getUserServiceUsageForService(apiUsage.service, apiUsage.user_id).then((response) => {
            let dailyData = getDailySum(response.data);
            let data = dailyData.map((detail) => {
                return { 'Day': detail[0], '# Request': detail[1] }
            });
            createDailyUsageChart(data);

            AdminInterface.instance.getUser(apiUsage.user_id).then((response) => setUserName(`${response.data.given_name} ${response.data.family_name}`)).catch((error) => console.log(error));

            AdminInterface.instance.getUserServiceUsageByLayer(apiUsage.user_id).then((response) => {
                let data = Object.entries(response.data).map(([key, value]) => { return { 'Mission': key, '# Request': value } });
                createMissionsUsageChart(data);
            });
        }).catch((error) => {
            props.setSelectedAPIUsage(null);
            withReactContent(Swal).fire({
                title: "Error",
                html: error.response.data?.detail,
            });
        });
    }

    useEffect(() => {
        if (props.selectedAPIUsage !== undefined) {
            GetAPIUsageDetail(props.selectedAPIUsage);
            setUserName(props.selectedAPIUsage?.user_id);
        }
    }, [props.selectedAPIUsage]);

    return (
        <Panel lg={12} md={12} title='User API Usages Details' subhead={`User: ${userName}`}
            dismissButton={() => handleModalClose()} refreshRequest={() => GetAPIUsageDetail(props.selectedAPIUsage)}>
            <Row>
                <Col lg={7}>
                    <h5>User Daily Usage for Service "{props.selectedAPIUsage?.service}"</h5>
                    <StyledPaper>
                        <div id="daily_usage_chart" className="chartdiv" />
                    </StyledPaper>
                </Col>
                <Col lg={5}>
                    <h5>User Usage by Missions</h5>
                    <StyledPaper>
                        <div id="mission_usage_chart" className="chartdiv" />
                    </StyledPaper>
                </Col>
            </Row>
        </Panel >
    );
};

export default APIUsageDetails;